import { useMutation } from '@apollo/client/react';
import { useSetAtom } from 'jotai';
import { useCallback, useRef, useState } from 'react';

import { BASE_DATE } from '~/constants/organization_notification';
import {
  ChargeNoAmountWithCashDocument,
  ChargeWithApp2Input,
  ChargeWithApp2LineItemInput,
  ChargeWithAppDocument,
  ChargeWithCash2Input,
  ChargeWithCashDocument,
  GetUncompletedAppointmentsNotificationsCountDocument,
  LineAppointmentForChargeFragment,
  LineItemCode,
  SkipEncounterDocument,
  UpdateDeliveryMethodDocument,
} from '~/graphql/generated/graphql';
import { usePlanFeature } from '~/hooks/use-plan-feature';
import { followupMessageSettingModalState } from '~/state/partials/followup_message_setting_modal/atoms';

import { Fields } from './types';

export const useCharge = () => {
  const { hasEnabledFeature: hasEnabledFollowup } = usePlanFeature('followup');
  const [error, setError] = useState<string | null>(null);

  // 二重呼び出しを防ぐためのref
  const isProcessingRef = useRef(false);

  const [skipEncounter, { loading: skipping }] = useMutation(SkipEncounterDocument, {
    // ネット受付の場合skipEncounter -> chargeと連続してリクエストする
    // その際にskipEncounterのレスポンスでキャッシュ更新走ってリストの再取得など走ると不都合がおきるのでno-cacheを指定した
    fetchPolicy: 'no-cache',
  });
  const [updateDeliveryMethod, { loading: changing }] = useMutation(UpdateDeliveryMethodDocument);
  const [chargeWithApp, { loading: chargingWithApp }] = useMutation(ChargeWithAppDocument, {
    refetchQueries: [
      {
        query: GetUncompletedAppointmentsNotificationsCountDocument,
        variables: { from: BASE_DATE },
      },
    ],
  });
  const [chargeWithCash, { loading: chargingWithCash }] = useMutation(ChargeWithCashDocument, {
    refetchQueries: [
      {
        query: GetUncompletedAppointmentsNotificationsCountDocument,
        variables: { from: BASE_DATE },
      },
    ],
  });
  const [chargeNoAmountWithCash] = useMutation(ChargeNoAmountWithCashDocument, {
    refetchQueries: [
      {
        query: GetUncompletedAppointmentsNotificationsCountDocument,
        variables: { from: BASE_DATE },
      },
    ],
  });

  // 薬歴連係で使用しているので、共通のstateを使用する
  const setModal = useSetAtom(followupMessageSettingModalState);

  const charge = useCallback(
    async (appointment: LineAppointmentForChargeFragment, values: Fields) => {
      // 既に処理中の場合は早期リターン
      if (isProcessingRef.current) {
        return;
      }

      // 処理開始をマーク
      isProcessingRef.current = true;

      const input: ChargeWithApp2Input | ChargeWithCash2Input = {
        appointmentId: appointment.id,
        lineItems: [values.lineItem, ...values.optionalLineItems].map<ChargeWithApp2LineItemInput>(
          (lineItem) => ({
            code: lineItem.code as LineItemCode,
            subject: lineItem.subject,
            amount: +lineItem.amount,
            chargeItems: [],
          }),
        ),
      };

      setError(null);

      if (!values.deliveryMethod) {
        const errorMessage = '配達方法が選択されていません';
        setError(errorMessage);
        throw new Error(errorMessage);
      }

      try {
        if (!appointment.telemedicine && appointment.status === 'booked') {
          await skipEncounter({
            variables: {
              input: {
                appointmentId: appointment.id,
              },
            },
          });
        }

        await updateDeliveryMethod({
          variables: {
            input: {
              appointmentId: appointment.id,
              deliveryMethod: values.deliveryMethod,
            },
          },
        });

        if (values.paymentMethod === 'app') {
          await chargeWithApp({ variables: { input } });
        } else if (values.paymentMethod === 'cash') {
          if (values.isNoAmount) {
            await chargeNoAmountWithCash({ variables: { input } });
          } else {
            await chargeWithCash({ variables: { input } });
          }
        } else {
          // おかしい
        }
      } catch (err) {
        const errorMessage = (err && err.message) || 'エラーが発生しました';
        setError(errorMessage);
        throw new Error(errorMessage, { cause: err });
      } finally {
        // 処理完了をマーク
        isProcessingRef.current = false;
      }

      if (hasEnabledFollowup) {
        setModal((_state) => ({
          ..._state,
          isOpen: true,
          guestPatientId: appointment.lineVisitor?.id || null,
        }));
      }
    },
    [
      chargeWithApp,
      chargeWithCash,
      chargeNoAmountWithCash,
      hasEnabledFollowup,
      setModal,
      skipEncounter,
      updateDeliveryMethod,
    ],
  );

  return {
    error,
    charging: chargingWithCash || chargingWithApp || skipping || changing,
    charge,
  };
};
